// 设备/布局判定 —— 全站手机版/PC 版切换的唯一真相源
//
// 判定优先级：
//   1. 用户手动切换（localStorage，"切换到电脑版/手机版" 按钮）—— 最高优先
//   2. 视口宽度 >= DESKTOP_MIN_WIDTH 走 PC 版，否则手机版
//
// 用 matchMedia 监听，resize / 旋转屏幕实时切换；手动切换写 localStorage 并广播。
//
// 阈值用 1024（lg）：横屏 iPad / 小笔记本算 PC，竖屏平板和手机算移动端。

import { ref, computed, onMounted, onUnmounted } from "vue";

export const DESKTOP_MIN_WIDTH = 1024;

const LAYOUT_KEY = "jzbro_layout"; // 'desktop' | 'mobile' | null(自动)
type LayoutOverride = "desktop" | "mobile" | null;

// 模块级单例状态：所有组件共享同一份，避免各自监听 resize
const viewportIsDesktop = ref(
  typeof window !== "undefined"
    ? window.innerWidth >= DESKTOP_MIN_WIDTH
    : true,
);
const override = ref<LayoutOverride>(
  typeof localStorage !== "undefined"
    ? (localStorage.getItem(LAYOUT_KEY) as LayoutOverride)
    : null,
);

let mql: MediaQueryList | null = null;
let listenerCount = 0;
function onMqlChange(e: MediaQueryListEvent | MediaQueryList) {
  viewportIsDesktop.value = e.matches;
}

/** 当前是否走 PC 版布局（手动覆盖优先，否则按视口） */
export const isDesktop = computed(() => {
  if (override.value === "desktop") return true;
  if (override.value === "mobile") return false;
  return viewportIsDesktop.value;
});

export const isMobile = computed(() => !isDesktop.value);

/** 视口本身够不够宽（不看手动覆盖）。用于"大屏却被锁成手机版"时显示切回入口 */
export const isWideScreen = computed(() => viewportIsDesktop.value);

/** 是否被用户手动锁定了某个布局 */
export const layoutOverridden = computed(() => override.value !== null);

/** 手动切到 PC 版 */
export function switchToDesktop() {
  override.value = "desktop";
  localStorage.setItem(LAYOUT_KEY, "desktop");
}
/** 手动切到手机版 */
export function switchToMobile() {
  override.value = "mobile";
  localStorage.setItem(LAYOUT_KEY, "mobile");
}
/** 取消手动锁定，回到按视口自动判定 */
export function clearLayoutOverride() {
  override.value = null;
  localStorage.removeItem(LAYOUT_KEY);
}

/** 组件里用：返回响应式的 isDesktop/isMobile + 切换方法。
 *  负责挂/卸 matchMedia 监听（引用计数，全局只挂一次）。 */
export function useDevice() {
  onMounted(() => {
    if (typeof window === "undefined") return;
    if (listenerCount === 0) {
      mql = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`);
      viewportIsDesktop.value = mql.matches;
      // Safari <14 用 addListener
      if (mql.addEventListener) mql.addEventListener("change", onMqlChange);
      else mql.addListener(onMqlChange);
    }
    listenerCount++;
  });
  onUnmounted(() => {
    listenerCount--;
    if (listenerCount === 0 && mql) {
      if (mql.removeEventListener) mql.removeEventListener("change", onMqlChange);
      else mql.removeListener(onMqlChange);
      mql = null;
    }
  });

  return {
    isDesktop,
    isMobile,
    isWideScreen,
    layoutOverridden,
    switchToDesktop,
    switchToMobile,
    clearLayoutOverride,
  };
}
